export enum CoverageCheckResponseType {
    COVERAGE = "COVERAGE",
    DISCOVER = "DISCOVER",
    SCAN = "SCAN",
    MEDICAID = "MEDICAID",
}

// Appointment workflow that triggered the coverage check
// Passed by integrations in request.custom_fields.workflow_type
export enum CoverageCheckWorkflowType {
    // Check runs when the appointment is created
    NEW_APPOINTMENTS = "new_appointments",
    // Check runs X days before the appointment date, only to widen the ERA match window
    UPCOMING_APPOINTMENTS = "upcoming_appointments",
}

export enum CalculatorResultType {
    unknownPlanStatus = "unknownPlanStatus",
    activeCoverage = "activeCoverage",
    activePartialCoverage = "activePartialCoverage",
    activePlanCoverageUnknown = "activePlanCoverageUnknown",
    noCoverage = "noCoverage",
    generalError = "generalError",
    memberNotFound = "memberNotFound",
    connectionIssue = "connectionIssue",
    payerNotSupported = "payerNotSupported",
    providerNotRecognized = "providerNotRecognized",
    errorRetrievingCoverage = "errorRetrievingCoverage",
    memberIdPayerIdInvalid = "memberIdPayerIdInvalid",
    invalidInput = "invalidInput",
    unexpectedError = "unexpectedError",
    tooManyRequests = "tooManyRequests",
    payerFailure = "payerFailure",
}

export enum AvailablePlanStatus {
    active = "ACTIVE",
    activeAtRisk = "ACTIVE_AT_RISK",
    inactive = "INACTIVE",
    notSupported = "NOT_SUPPORTED",
    unknown = "UNKNOWN",
}

export enum LegalGender {
    male = "M",
    female = "F",
}

export enum PayerCoverageCheckNetwork {
    IN = "IN",
    OUT = "OUT",
    IN_OUT = "IN_OUT",
}

export enum AvailableModalityCoverageStatus {
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE",
    UNKNOWN = "UNKNOWN",
}

export enum AvailablePriorAuthorizationIndication {
    REQUIRED = "REQUIRED",
    NOT_REQUIRED = "NOT_REQUIRED",
    UNKNOWN = "UNKNOWN",
}

export enum AvailablePlanType {
    COMMERCIAL = "COMMERCIAL",
    DUAL = "DUAL",
    GOVERNMENT = "GOVERNMENT",
    MARKETPLACE = "MARKETPLACE",
    ORIGINAL_MEDICARE = "ORIGINAL_MEDICARE",
    MEDICARE_ADVANTAGE = "MEDICARE_ADVANTAGE",
    ORIGINAL_MEDICAID = "ORIGINAL_MEDICAID",
    MANAGED_MEDICAID = "MANAGED_MEDICAID",
    UNKNOWN = "UNKNOWN",
    SUPPLEMENTAL_MEDICARE = "SUPPLEMENTAL_MEDICARE",
    VETERANS_AFFAIRS = "VETERANS_AFFAIRS",
    EMPLOYEE_ASSISTANCE_PROGRAM = "EMPLOYEE_ASSISTANCE_PROGRAM",

    // custom value that is not in the original enum, used in the code for filtering null values
    NOT_APPLICABLE = "NOT_APPLICABLE",
}

export enum MedicaidPlanStatus {
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE",
}

export enum BlueCardEligibility {
    ELIGIBLE = "ELIGIBLE",
    NOT_ELIGIBLE = "NOT_ELIGIBLE",
}

export enum AvailableInsuranceType {
    HMO = "HMO",
    PPO = "PPO",
    EPO = "EPO",
    POS = "POS",
    INDEMNITY = "INDEMNITY",
    HMO_POS = "HMO_POS",
    HDHP = "HDHP",
}

export enum AvailableCoverageErrorCode {
    payerNotSupported = "PAYER_NOT_SUPPORTED",
    payerFailure = "PAYER_FAILURE",
    memberNotFound = "MEMBER_NOT_FOUND",
    memberIdPayerIdInvalid = "MEMBER_ID_PAYER_ID_INVALID",
    errorRetrievingCoverage = "ERROR_RETRIEVING_COVERAGE",
    providerNotRecognized = "PROVIDER_NOT_RECOGNIZED",
    tooManyRequests = "TOO_MANY_REQUESTS",
    unexpectedError = "UNEXPECTED_ERROR",
    invalidInput = "INVALID_INPUT",
    errorProcessingFinancials = "ERROR_PROCESSING_FINANCIALS",
    unknownPlanStatus = "UNKNOWN_PLAN_STATUS",
    unknownRemainings = "UNKNOWN_REMAININGS",
    unknownCostshare = "UNKNOWN_COSTSHARE",
}

export enum AvailableThirdPartyPayerType {
    PAYER = "PAYER",
    UTILIZATION_MANAGER = "UTILIZATION_MANAGER",
}

export enum ResetBenefitStatus {
    DETECTED_ACTION_REQUIRED = "DETECTED_ACTION_REQUIRED",
    DETECTED_NO_ACTION_REQUIRED = "DETECTED_NO_ACTION_REQUIRED",
    NOT_DETECTED = "NOT_DETECTED",
}

export const COMMON_COVERAGE_CONFIG = {
    defaultHealthProviderRate: 200, // in $

    get defaultHealthProviderRateCents(): number {
        return COMMON_COVERAGE_CONFIG.defaultHealthProviderRate * 100
    },

    defaultCptCodeWithoutModality: "90834",

    // Workflows whose checks exist only to widen the ERA match window. They are kept out of
    // latest_coverage, so they never surface in WMS and must not be treated as the check a
    // user acts on, even though they can be the newest check a policy has.
    //
    // IMPORTANT: hardcoded copies of this list live outside the monorepo build:
    // - scripts/mongo-atlas/functions/latest_process_coverage.js (Atlas coverage trigger)
    // - scripts/mongo-atlas/functions/latest_process_policies.js (Atlas policies trigger)
    // - scripts/mongosh/latest-coverage/latestCoverage_core.js (mongosh + daily refresh cron)
    get latestCoverageExcludedWorkflowTypes(): CoverageCheckWorkflowType[] {
        return [CoverageCheckWorkflowType.UPCOMING_APPOINTMENTS]
    },

    get availableInsuranceTypeMapping(): Record<
        AvailableInsuranceType,
        string
    > {
        return {
            [AvailableInsuranceType.HMO]: "HMO",
            [AvailableInsuranceType.PPO]: "PPO",
            [AvailableInsuranceType.EPO]: "EPO",
            [AvailableInsuranceType.POS]: "POS",
            [AvailableInsuranceType.INDEMNITY]: "Indemnity",
            [AvailableInsuranceType.HMO_POS]: "HMO-POS",
            [AvailableInsuranceType.HDHP]: "HDHP",
        }
    },

    get availablePlanStatusMapping(): Record<AvailablePlanStatus, string> {
        return {
            [AvailablePlanStatus.active]: "Active",
            [AvailablePlanStatus.activeAtRisk]: "Active at Risk",
            [AvailablePlanStatus.inactive]: "Inactive",
            [AvailablePlanStatus.unknown]: "Unknown",
            [AvailablePlanStatus.notSupported]: "Payer not Supported",
        }
    },

    get mainCoverageResponseTypes(): CoverageCheckResponseType[] {
        return [
            CoverageCheckResponseType.COVERAGE,
            CoverageCheckResponseType.MEDICAID,
        ]
    },

    get responseTypeMapping(): Record<CoverageCheckResponseType, string> {
        return {
            [CoverageCheckResponseType.COVERAGE]: "Standard",
            [CoverageCheckResponseType.MEDICAID]: "Medicaid",
            [CoverageCheckResponseType.DISCOVER]: "Discover",
            [CoverageCheckResponseType.SCAN]: "Scan",
        }
    },

    get bluecardEligibilityMapping(): Record<BlueCardEligibility, string> {
        return {
            [BlueCardEligibility.ELIGIBLE]: "Eligible",
            [BlueCardEligibility.NOT_ELIGIBLE]: "Not Eligible",
        }
    },
}
